"use client";

import * as React from "react";
import * as RDialog from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { cn } from "./cn";

export type DialogProps = {
  open: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  description?: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
  size?: "sm" | "md" | "lg";
  hideClose?: boolean;
};

const SIZES = {
  sm: "max-w-md",
  md: "max-w-lg",
  lg: "max-w-2xl",
} as const;

export function Dialog({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = "md",
  hideClose,
}: DialogProps) {
  return (
    <RDialog.Root
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <RDialog.Portal>
        <RDialog.Overlay className="fixed inset-0 z-50 bg-ink/60 backdrop-blur-sm data-[state=open]:animate-in data-[state=open]:fade-in" />
        <RDialog.Content
          className={cn(
            "fixed left-1/2 top-1/2 z-50 w-[calc(100%-1rem)] -translate-x-1/2 -translate-y-1/2",
            "flex max-h-[calc(100dvh-2rem)] flex-col bg-paper rounded-[24px] shadow-xl outline-none sm:w-[calc(100%-2rem)]",
            "data-[state=open]:animate-in data-[state=open]:fade-in data-[state=open]:zoom-in-95",
            SIZES[size],
          )}
        >
          {(title || description || !hideClose) && (
            <div className="flex shrink-0 items-start justify-between gap-3 px-5 pb-3 pt-5 sm:px-6 sm:pt-6">
              <div className="min-w-0 flex-1">
                {title ? (
                  <RDialog.Title className="text-[18px] font-extrabold tracking-tight text-ink sm:text-[20px]">
                    {title}
                  </RDialog.Title>
                ) : null}
                {description ? (
                  <RDialog.Description className="mt-1 text-sm text-ash">
                    {description}
                  </RDialog.Description>
                ) : null}
              </div>
              {!hideClose ? (
                <RDialog.Close
                  aria-label="Đóng"
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-mist text-ink transition-colors hover:bg-smoke"
                >
                  <X size={16} strokeWidth={2} />
                </RDialog.Close>
              ) : null}
            </div>
          )}
          <div className="flex-1 overflow-y-auto px-5 pb-5 sm:px-6 sm:pb-6">
            {children}
          </div>
          {footer ? (
            <div className="flex shrink-0 flex-wrap items-center justify-end gap-2 border-t border-smoke bg-paper px-5 py-4 sm:px-6">
              {footer}
            </div>
          ) : null}
        </RDialog.Content>
      </RDialog.Portal>
    </RDialog.Root>
  );
}
